/* ───────────────────────────────────────────────────────────
   src/pages/.../CostingRatesSetup.jsx   – 100 % fetch version
   ─────────────────────────────────────────────────────────── */
   import React, { useState, useEffect, useCallback } from "react";
   import API_BASE_URL from "../../../../../../Constants";
   
   import {
     Box, Table, TableHead, TableBody, TableRow, TableCell,
     TextField, MenuItem, IconButton, Tooltip,
     Snackbar, Alert, InputAdornment
   } from "@mui/material";
   
   import ModeIcon          from "@mui/icons-material/ModeOutlined";       // ✏️
   import SaveOutlinedIcon  from "@mui/icons-material/SaveOutlined";       // 💾
   import AddIcon           from "@mui/icons-material/AddOutlined";        // ➕
   import RefreshIcon       from "@mui/icons-material/RefreshOutlined";    // 🗘
   import CloseIcon         from "@mui/icons-material/CloseOutlined";      // ×
   import DeleteIcon        from "@mui/icons-material/DeleteOutlineOutlined";
   
   /* ── tiny helper around fetch ─────────────────────────────── */
   const fetchJSON = async (url, { method = "GET", body } = {}) => {
     const res = await fetch(url, {
       method,
       credentials: "include",
       headers: { "Content-Type": "application/json" },
       body: body ? JSON.stringify(body) : undefined
     });
     if (!res.ok) {
       const msg = (await res.text().catch(()=>"")) || res.statusText;
       throw new Error(msg);
     }
     return res.status === 204 ? null : res.json();
   };
   
   /* constants ------------------------------------------------- */
   const EMPLOYEE_TYPES = [
     "Accounting","Construction","Inspections / Violations","Maintenance",
     "Management","Modernization","Office","Proposals / Jobs",
     "Sales","Service / Repair","Warehouse / Shop"
   ];
   
   const COLS = [
     { key:"employeeType", label:"Employee Type", flex:2 },
     { key:"standardCost", label:"Standard Cost", flex:1 , type:"money"},
     { key:"otAmEnd",      label:"OT AM End",     flex:1 , type:"time"},
     { key:"otPmStart",    label:"OT PM Start",   flex:1 , type:"time"},
     { key:"hrsPerDay",    label:"Hrs/Day",       flex:.8, type:"time"},   // ← reverted
     { key:"hrsPerWeek",   label:"Hrs/Wk",        flex:.8, type:"time"},   // ← reverted
     { key:"payCode",      label:"Pay Code",      flex:1  }
   ];
   
   const TEMPLATE = COLS.reduce((m,c)=>({...m,[c.key]:""}),{});
   const uid = (()=>{let n=0;return()=>`tmp-${++n}`})();   // tmp-ids
   
   /* component ------------------------------------------------- */
   export default function CostingRatesSetup({ companyId = 1, onClose = null }) {
   
     const [rows,     setRows]   = useState([]);
     const [deleted,  setDeleted]= useState([]);
     const [dirty,    setDirty]  = useState(false);
     const [isEditing,setEdit]   = useState(false);
     const [saving,   setSaving] = useState(false);
     const [msg,      setMsg]    = useState(null);
   
     /* load rows ----------------------------------------------- */
     const load = useCallback(async () => {
       try {
         const data = await fetchJSON(`${API_BASE_URL}/api/costing-rates/company/${companyId}`);
         setRows(data || []);
         setDeleted([]); setDirty(false); setEdit(false);
       } catch (err) {
         setMsg({ severity:"error", text:`Load failed: ${err.message}` });
       }
     }, [companyId]);
   
     useEffect(() => { load(); }, [load]);
   
     /* helpers -------------------------------------------------- */
     const markDirty = () => !dirty && setDirty(true);
   
     const updateField = (idx,key,val) => {
       setRows(p => p.map((r,i)=> i===idx ? { ...r, [key]: val } : r));
       markDirty();
     };
   
     /* add / delete -------------------------------------------- */
     const addRow = () => {
       setRows(p => [ { ...TEMPLATE, id: uid(), companyId }, ...p ]);
       setEdit(true);
       markDirty();
     };
   
     const deleteRow = idx => () => {
       setRows(p => {
         const copy = [...p]; const [victim] = copy.splice(idx,1);
         if(victim?.id && !String(victim.id).startsWith("tmp"))
           setDeleted(d => [...d,victim.id]);
         return copy;
       });
       markDirty();
     };
   
     /* persist -------------------------------------------------- */
     const persist = async () => {
       if (saving) return;
       setSaving(true);
       try {
         /* 1. deletes */
         await Promise.all(
           deleted.map(id =>
             fetchJSON(`${API_BASE_URL}/api/costing-rates/${id}`, { method:"DELETE" })
           )
         );
   
         /* 2. inserts & updates */
         await Promise.all(rows.map(r => {
           const payload = { ...r, companyId };
   
           // sanitise numeric fields
           ["standardCost"].forEach(k=>{                       // ← hrsPer* removed
             if(payload[k]==="") payload[k]=null;
             else if(payload[k]!==null && payload[k]!==undefined) payload[k]=Number(payload[k]);
           });
   
           if(String(r.id).startsWith("tmp")){
             delete payload.id;
             return fetchJSON(`${API_BASE_URL}/api/costing-rates`, { method:"POST", body:payload });
           }
           return fetchJSON(`${API_BASE_URL}/api/costing-rates/${r.id}`, { method:"PUT", body:payload });
         }));
   
         await load();
         setMsg({ severity:"success", text:"Saved" });
       } catch (err) {
         setMsg({ severity:"error", text:`Save failed: ${err.message}` });
       } finally { setSaving(false); }
     };
   
     /* cell renderers ------------------------------------------ */
     const viewCell = (val,w)=>(<div className="bg-gray-50 px-2 py-1 rounded text-sm"
                                     style={{minWidth:w}}>
       {val || <span className="text-gray-400">—</span>}
     </div>);
   
     const renderCell = (idx, c) => {
       const w = 100;
       if (!isEditing) return viewCell(rows[idx][c.key], w);
   
       const common = {
         variant:"standard",
         value: rows[idx][c.key] ?? "",
         onChange: e => updateField(idx, c.key, e.target.value),
         sx:{ width:"100%" }
       };
   
       if (c.key === "employeeType")
         return (
           <TextField select {...common}>
             {EMPLOYEE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
           </TextField>
         );
   
       if (c.type === "time")
         return <TextField {...common} type="time" inputProps={{ step:900 }} />;
   
       if (c.type === "money")
         return (
           <TextField {...common} type="number"
             InputProps={{
               startAdornment:<InputAdornment position="start">$</InputAdornment>,
               step:0.01
             }} />
         );
   
       return <TextField {...common} type={c.type || "text"} />;
     };
   
     /* render --------------------------------------------------- */
     return (
       <Box p={2} sx={{ display:"flex", gap:2, overflowX:"auto" }}>
         <Box sx={{ flex:2, minWidth:760 }}>
           {/* toolbar */}
           <Box mb={1} display="flex" gap={2}>
             {/* EDIT ↔ SAVE */}
             <Tooltip title={isEditing ? "Save changes" : "Edit"}>
               <span>
                 <IconButton size="small" disabled={saving}
                   onClick={async()=>{
                     if (isEditing) { await persist(); setEdit(false); }
                     else            { setEdit(true); }
                   }}>
                   {isEditing
                     ? <SaveOutlinedIcon fontSize="small"
                         className={saving ? "text-gray-400" : "text-blue-600"} />
                     : <ModeIcon fontSize="small" className="text-gray-700" />}
                 </IconButton>
               </span>
             </Tooltip>
   
             {/* ADD */}
             <Tooltip title="Add row">
               <IconButton size="small" disabled={saving} onClick={addRow}>
                 <AddIcon fontSize="small" />
               </IconButton>
             </Tooltip>
   
             {/* REFRESH */}
             <Tooltip title="Reload list">
               <IconButton size="small" disabled={saving} onClick={load}>
                 <RefreshIcon fontSize="small" />
               </IconButton>
             </Tooltip>
   
             {/* CLOSE */}
             {onClose && (
               <Tooltip title="Close">
                 <IconButton size="small" onClick={onClose}>
                   <CloseIcon fontSize="small" />
                 </IconButton>
               </Tooltip>
             )}
   
             {saving && <span className="text-xs text-gray-500 ml-2">Saving…</span>}
           </Box>
   
           {/* table */}
           <Table size="small">
             <TableHead>
               <TableRow>
                 {COLS.map(col => (
                   <TableCell key={col.key} sx={{ fontWeight:600 }}>
                     {col.label}
                   </TableCell>
                 ))}
                 <TableCell />
               </TableRow>
             </TableHead>
             <TableBody>
               {rows.map((r,i) => (
                 <TableRow key={r.id} hover>
                   {COLS.map(c => (
                     <TableCell key={c.key}>{renderCell(i,c)}</TableCell>
                   ))}
                   <TableCell>
                     <Tooltip title="Delete row">
                       <span>
                         <IconButton size="small"
                                     disabled={saving || !isEditing}
                                     onClick={deleteRow(i)}>
                           <DeleteIcon fontSize="inherit" />
                         </IconButton>
                       </span>
                     </Tooltip>
                   </TableCell>
                 </TableRow>
               ))}
             </TableBody>
           </Table>
         </Box>
   
         {/* snackbar */}
         <Snackbar open={!!msg} autoHideDuration={4000}
                   onClose={()=>setMsg(null)}
                   anchorOrigin={{ vertical:"bottom", horizontal:"center" }}>
           {msg && <Alert severity={msg.severity} variant="filled">{msg.text}</Alert>}
         </Snackbar>
       </Box>
     );
   }
   